import { useEffect } from 'react';

type KeyHandler =
    | (() => void)
    | {
          handler: () => void;
          condition: () => boolean;
      };

interface KeyHandlers {
    escape?: KeyHandler;
    enter?: KeyHandler;
    backspace?: KeyHandler;
    p?: KeyHandler;
    f?: KeyHandler;
}

const NATIVELY_ACTIVATED =
    'a[href], area[href], button, input, select, textarea, summary, [contenteditable]:not([contenteditable="false"])';

let enterFallbackInstalled = false;

const enterFallback = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' || event.defaultPrevented) return;

    const { activeElement } = document;

    if (!(activeElement instanceof HTMLElement) || activeElement === document.body) return;
    if (activeElement.matches(NATIVELY_ACTIVATED)) return;

    activeElement.click();
};

const installEnterFallback = () => {
    if (enterFallbackInstalled) return;

    enterFallbackInstalled = true;
    window.addEventListener('keydown', enterFallback);
};

export const useKeyboardShortcuts = (handlers: KeyHandlers) => {
    useEffect(() => {
        installEnterFallback();
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const handler = (() => {
                switch (event.key) {
                    case 'Escape':
                        return handlers.escape;
                    case 'Enter':
                        return handlers.enter;
                    case 'Backspace':
                        return handlers.backspace;
                    case 'p':
                        return handlers.p;
                    case 'f':
                        return handlers.f;
                    default:
                        return undefined;
                }
            })();

            if (!handler) return;

            // Ignore keyboard shortcuts when the user is typing in an input field (e.g. Chrome's Cmd+F search bar),
            // otherwise keys like 'f' or 'p' would trigger EPG actions instead of being typed as text.
            const tag = (document.activeElement?.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || (document.activeElement as HTMLElement)?.isContentEditable) {
                return;
            }

            if (typeof handler === 'function') {
                handler();
            } else if (handler.condition()) {
                handler.handler();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handlers]);
};
